import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LinkIcon from '@mui/icons-material/Link';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  ChipProps,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnapshotCreateMutation } from '../api/snapshot.js';
import { useTestReadQuery } from '../api/test.js';
import { AnalysisList } from './AnalysisList.js';
import { DependencyList } from './DependencyList.js';
import { DeploymentList } from './DeploymentList.js';
import { SnapshotList } from './SnapshotList.js';

type TestViewParams = {
  projectId: string;
  testId: string;
};

function getStatusColor(status: string): ChipProps['color'] {
  switch (status) {
    case 'DEFAULT':
      return 'success';
    case 'CRASHING':
      return 'error';
    case 'UNREACHABLE':
      return 'warning';
    case 'SKIPPED':
      return 'info';
    case 'BLOCKED':
      return 'warning';
    default:
      return 'default';
  }
}

export function TestView() {
  const { projectId, testId } = useParams<TestViewParams>();
  if (!testId || !projectId) throw new Error('Missing parameter');

  const [value, setValue] = useState(0);
  const snapshotCreate = useSnapshotCreateMutation('web');
  const { enqueueSnackbar } = useSnackbar();
  const handleChange = (event: any, newValue: number) => {
    setValue(newValue);
  };

  const handleSnapshotClick = () => {
    snapshotCreate.mutate(
      { projectId, resourceId: testId, settings: { recordTrace: true, takeScreenshot: true } },
      {
        onSuccess: () => enqueueSnackbar('Snapshot job submitted successfully', { variant: 'success' }),
        onError: (err) =>
          enqueueSnackbar(
            `Error: Failed to submit snapshot job (${err instanceof Error ? err.message : String(err)})`,
            { variant: 'error' }
          )
      }
    );
  };

  const test = useTestReadQuery(projectId, testId);
  if (test.isLoading) return <CircularProgress />;
  if (test.isError) return <Alert severity="error">{`Error: ${test.error.message}`}</Alert>;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <Typography variant="h5">Test {test.data.name}</Typography>
        <Button
          variant="contained"
          startIcon={<CameraAltIcon />}
          onClick={handleSnapshotClick}
          disabled={snapshotCreate.isLoading}
        >
          Take Web Snapshot
        </Button>
      </Box>

      <TabContext value={value.toString()}>
        <TabList value={value} onChange={handleChange}>
          <Tab label="Snapshots" value="0" />
          <Tab label="Analyses" value="1" />
          <Tab label="Dependencies" value="2" />
          <Tab label="Deployments" value="3" />
          <Tab label="Details" value="4" />
        </TabList>

        <TabPanel value="0">
          <SnapshotList type="web" projectId={projectId} resourceId={testId} />
        </TabPanel>

        <TabPanel value="1">
          <AnalysisList type="web" projectId={projectId} resourceId={testId} />
        </TabPanel>

        <TabPanel value="2">
          <DependencyList projectId={projectId} testId={testId} />
        </TabPanel>

        <TabPanel value="3">
          <DeploymentList projectId={projectId} testId={testId} />
        </TabPanel>

        <TabPanel value="4">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Test Details
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography variant="h6" component="div">
                  {test.data.name}
                </Typography>
                <Chip
                  size="small"
                  label={test.data.enabled ? 'Active' : 'Disabled'}
                  color={test.data.enabled ? 'success' : 'default'}
                />
                <Chip size="small" label={test.data.status} color={getStatusColor(test.data.status)} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                ID: {test.data.id}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, mb: 1 }}>
              <LinkIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {test.data.target}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">{new Date(test.data.createdDate).toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">{new Date(test.data.updatedDate).toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Failures
                </Typography>
                <Typography variant="body2">{test.data.failures ?? 0}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Actions Recorded
                </Typography>
                <Typography variant="body2">{test.data.actions ? 'Yes' : 'No'}</Typography>
              </Box>
            </Box>

            {test.data.actions && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Actions
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: 'background.default',
                    p: 1.5,
                    borderRadius: 1,
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    mb: 0
                  }}
                >
                  {test.data.actions}
                </Box>
              </Box>
            )}

            {test.data.initScript && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Init Script
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: 'background.default',
                    p: 1.5,
                    borderRadius: 1,
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    mb: 0
                  }}
                >
                  {test.data.initScript}
                </Box>
              </Box>
            )}
          </Paper>
        </TabPanel>
      </TabContext>
    </Box>
  );
}
